/* eslint-disable avoma/use-svgr-over-svg */
import { ECrmFieldEditAccess } from 'types/crm';
import { TDealRiskLevel } from 'types/deals';

import { L10nAIWinLossAnalysis } from 'helpers/l10n/L10nAIWinLoss';
import { isAvoma } from 'helpers/user';

import copilotIcon from 'images/ic_ai_copilot.svg';
import aiMethodologyIcon from 'images/ic_ai_methodology.svg';
import { ReactComponent as CheckIcon } from 'images/ic_available_check.svg';
import bulletListIcon from 'images/ic_bullet_list.svg';
import commentIcon from 'images/ic_comment_gray.svg';
import dealHealthIcon from 'images/ic_deal_health.svg';
import { ReactComponent as ErrorIcon } from 'images/ic_error.svg';
import propertiesIcon from 'images/ic_properties.svg';
import scorecardIcon from 'images/ic_scorecard.svg';
import aiAnalysisIcon from 'images/ic_smart_playlists_grey.svg';
import activityIcon from 'images/ic_tab_activity.svg';
import { ReactComponent as WarningIcon } from 'images/ic_warning_solid.svg';

export const COMING_SOON_FIELDS: string[] = [];
export const HIDDEN_FIELDS = ['crmtaskfield'];
export const MAPPING_DISABLED_FIELDS = ['crmcontactfield', 'crmleadfield'];

export const DEAL_HEALTH_COLUMN = {
  uuid: 'deal_health',
  order: 1,
  groupName: 'Deal',
  label: 'Deal Health',
  isCrmData: false,
  dataType: 'string',
  fieldType: 'dealHealth',
  isEditable: false,
  isReadOnly: true,
  editableBy: ECrmFieldEditAccess.viewOnly,
  sortKey: null,
  options: [],
  objectType: null,
  avomaField: null,
  orderBy: null,
  referenceKey: null,
  isSummarized: false
};

export const AI_METHODOLOGY_COLUMN = {
  uuid: 'ai_methodology',
  order: 3,
  groupName: 'Deal',
  label: 'Methodology Qualification',
  isCrmData: false,
  dataType: 'string',
  fieldType: 'aiMethodology',
  isEditable: false,
  isReadOnly: true,
  editableBy: ECrmFieldEditAccess.viewOnly,
  sortKey: null,
  options: [],
  objectType: null,
  avomaField: null,
  orderBy: null,
  referenceKey: null,
  isSummarized: false
};
export const AI_METHODOLOGY_SCORE_COLUMN = {
  uuid: 'ai_methodology_score',
  order: 2,
  groupName: 'Deal',
  label: 'Qualification Score',
  isCrmData: false,
  dataType: 'string',
  fieldType: 'aiMethodology',
  isEditable: false,
  isReadOnly: true,
  editableBy: ECrmFieldEditAccess.viewOnly,
  sortKey: null,
  options: [],
  objectType: null,
  avomaField: null,
  orderBy: {
    ruleUid: 'deal_qualification_score',
    ruleObjectType: null
  },
  referenceKey: null,
  isSummarized: false
};

export const DEAL_RISK_COLUMN = {
  uuid: 'deal_risk',
  order: 0,
  groupName: 'Deal',
  label: 'Risk Score',
  isCrmData: false,
  dataType: 'number',
  fieldType: 'dealRisk',
  isEditable: false,
  isReadOnly: true,
  editableBy: ECrmFieldEditAccess.viewOnly,
  sortKey: 'risk_score',
  options: [],
  objectType: null,
  avomaField: null,
  orderBy: {
    ruleUid: 'deal_risk',
    ruleObjectType: null
  },
  referenceKey: null,
  isSummarized: false
};

export const CUSTOM_CATEGORY = 'custom category';
export const SMART_CATEGORY = 'Smart Topic';

export const ENGAGEMENT_COLUMN_TIME_OPTIONS = [
  { label: 'D', value: 'day', tooltip: 'Day view' },
  { label: 'W', value: 'week', tooltip: 'Week view' },
  { label: 'M', value: 'month', tooltip: 'Month view' }
];

export const DEAL_QUICKVIEW_TABS = [
  { label: 'Deal Health', value: 'alerts', iconPath: dealHealthIcon },
  { label: 'Activity', value: 'activity', iconPath: activityIcon },
  { label: 'AI Analysis', value: 'ai-analysis', iconPath: aiAnalysisIcon },
  { label: 'Properties', value: 'properties', iconPath: propertiesIcon },
  { label: 'Comments', value: 'comments', iconPath: commentIcon },
  {
    label: 'Ask Avoma',
    value: 'ask-avoma',
    iconPath: copilotIcon
  }
];

export const DEAL_QUICKVIEW_AI_METHODOLOGY_TAB = {
  label: 'Qualification',
  value: 'ai-methodology',
  iconPath: aiMethodologyIcon
};

export const COMPANY_QUICKVIEW_TABS = [
  { label: 'Activity', value: 'activity', iconPath: activityIcon },
  { label: 'Properties', value: 'properties', iconPath: propertiesIcon },
  {
    label: 'Ask Avoma',
    value: 'ask-avoma',
    iconPath: copilotIcon
  }
];

export const CONTACT_QUICKVIEW_TABS = [
  { label: 'Activity', value: 'activity', iconPath: activityIcon },
  { label: 'Properties', value: 'properties', iconPath: propertiesIcon }
];

export const LEAD_QUICKVIEW_TABS = [
  { label: 'Activity', value: 'activity', iconPath: activityIcon },
  { label: 'Properties', value: 'properties', iconPath: propertiesIcon }
];

export const AI_WIN_LOSS_ANALYSIS_STATES = {
  NOT_CREATED: 'not_created',
  CREATED: 'created',
  IN_PROGRESS: 'inprogress',
  DONE: 'done',
  ERROR: 'error'
};

export const AI_WIN_LOSS_ANALYSIS_MESSAGES_FOR_ERROR_STATES: Record<
  string,
  React.ReactNode
> = {
  no_engagements: L10nAIWinLossAnalysis.errors.noEngagements(),
  no_associated_domain: L10nAIWinLossAnalysis.errors.noAssociatedDomain(),
  own_no_feat: L10nAIWinLossAnalysis.errors.ownNoFeat()
};

export const DEAL_RISK_LEVELS: Record<
  TDealRiskLevel,
  {
    label: string;
    key: TDealRiskLevel;
    color: string;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    background: string;
    border: string;
  }
> = {
  high: {
    label: 'High Risk',
    key: 'high',
    color: 'text-red-dark',
    icon: ErrorIcon,
    background: 'bg-red-lightest',
    border: 'border-red-light border-opacity-50'
  },
  medium: {
    label: 'Medium Risk',
    key: 'medium',
    color: 'text-yellow-darker',
    icon: WarningIcon,
    background: 'bg-[#FFF3CD]',
    border: 'border-yellow-light border-opacity-80'
  },
  low: {
    label: 'Low Risk',
    key: 'low',
    color: 'text-green-dark',
    icon: CheckIcon,
    background: 'bg-green-lightest',
    border: 'border-green-light border-opacity-50'
  }
};

export const DEAL_SCORECARD_SEGMENT_COLORS = {
  0: 'bg-[#ECABA1] text-[#30100D]',
  1: 'bg-[#F5D1CC] text-[#501C16]',
  2: 'bg-red-lightest text-red-dark',
  3: 'bg-[#FFF3CD] text-yellow-darker',
  4: 'bg-green-lightest text-green-dark',
  5: 'bg-[#B1E7C8] text-[#16311E]'
};

export const dealScorecardRatingColorGradient = new Map([
  [2, [DEAL_SCORECARD_SEGMENT_COLORS[1], DEAL_SCORECARD_SEGMENT_COLORS[5]]],
  [
    3,
    [
      DEAL_SCORECARD_SEGMENT_COLORS[1],
      DEAL_SCORECARD_SEGMENT_COLORS[3],
      DEAL_SCORECARD_SEGMENT_COLORS[5]
    ]
  ],
  [
    4,
    [
      DEAL_SCORECARD_SEGMENT_COLORS[1],
      DEAL_SCORECARD_SEGMENT_COLORS[2],
      DEAL_SCORECARD_SEGMENT_COLORS[3],
      DEAL_SCORECARD_SEGMENT_COLORS[5]
    ]
  ],
  [
    5,
    [
      DEAL_SCORECARD_SEGMENT_COLORS[1],
      DEAL_SCORECARD_SEGMENT_COLORS[2],
      DEAL_SCORECARD_SEGMENT_COLORS[3],
      DEAL_SCORECARD_SEGMENT_COLORS[4],
      DEAL_SCORECARD_SEGMENT_COLORS[5]
    ]
  ],
  [
    6,
    [
      DEAL_SCORECARD_SEGMENT_COLORS[0],
      DEAL_SCORECARD_SEGMENT_COLORS[1],
      DEAL_SCORECARD_SEGMENT_COLORS[2],
      DEAL_SCORECARD_SEGMENT_COLORS[3],
      DEAL_SCORECARD_SEGMENT_COLORS[4],
      DEAL_SCORECARD_SEGMENT_COLORS[5]
    ]
  ]
]);

export const AI_METHODOLOGY_TAB_OPTIONS = [
  {
    id: 'summary',
    name: 'Summary',
    label: 'Summary',
    value: 'summary',
    iconPath: bulletListIcon
  },
  {
    id: 'justification',
    name: 'Score Insights',
    label: 'Score Insights',
    value: 'justification',
    iconPath: scorecardIcon
  }
];
